import { SortTypeEnum } from '@/request/request.enum';
import { ITrendingReq, TokenTimeRange } from '@/request/token/token.request.interface';
import { useEffect, useMemo, useRef, useState } from 'react';
import BigNumber from 'bignumber.js';
import tokenApiService from '@/request/token/token.api.service';
import { ITrending } from '@/request/token/token.response.interface';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import userApiService from '@/request/user/user.api.service';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CustomTab from '@/widget/CustomTab';
import { useTranslation } from 'react-i18next';
import TokenHead from './components/TokenList/TokenHead';
import TokenList from './components/TokenList';
import AccountInfo from './components/AccountInfo';
import { useAppStore } from '@/store';
import { TOKEN_TIME_TAB_CONFIG } from './config';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';

type TabType = 'trending' | 'watchList';

export interface SortConfig {
  key: 'price' | 'time' | 'vol' | 'mc';
  direction: SortTypeEnum;
}

const ONE_BILLION = 1e9;

export default () => {
  const { setFollowTokenAddressList } = useAppStore();
  const { t } = useTranslation();
  const { subscribe } = useAppEventBusContext();
  const { walletAddress, isConnected, chain } = useBoomWallet();
  const [timeRange, setTimeRange] = useState<TokenTimeRange>(TokenTimeRange['24h']);
  const [activeIndex, setActiveIndex] = useState(0);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState(true);
  const [trendingList, setTrendingList] = useState<ITrending[]>([]);
  const [followList, setFollowList] = useState<ITrending[]>([]);

  const [sortConfigs, setSortConfigs] = useState<Record<TabType, SortConfig>>({
    trending: { key: 'vol', direction: SortTypeEnum.DESC },
    watchList: { key: 'vol', direction: SortTypeEnum.DESC },
  });

  const tabs = useMemo(() => {
    const defaultTabs = [t('Token.trending')];
    if (isConnected) {
      defaultTabs.push(t('Token.watchList'));
    }
    return defaultTabs;
  }, [isConnected, t]);

  const currentTab = useMemo<TabType>(() => {
    return ['trending', 'watchList'][activeIndex] as TabType;
  }, [activeIndex, tabs]);

  const queryParams: ITrendingReq = useMemo(
    () => ({
      page: 1,
      limit: 100,
      timeRange,
      tab: 'trending',
    }),
    [timeRange],
  );

  const compareValues = (a: ITrending, b: ITrending, key: SortConfig['key']): number => {
    const getValue = (item: ITrending): BigNumber => {
      switch (key) {
        case 'price':
          return new BigNumber(item.price);
        case 'time':
          return new BigNumber(item.priceChange);
        case 'vol':
          return new BigNumber(item.volume);
        case 'mc':
          const supply = item.totalSupply === '0' ? ONE_BILLION : item.totalSupply;
          return new BigNumber(supply).times(item.price);
        default:
          return new BigNumber(0);
      }
    };
    const valueA = getValue(a);
    const valueB = getValue(b);
    return sortConfigs[currentTab].direction === SortTypeEnum.ASC ? valueA.minus(valueB).toNumber() : valueB.minus(valueA).toNumber();
  };

  const handleSort = (key: SortConfig['key']) => {
    setSortConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        key,
        direction: prev[currentTab].key === key && prev[currentTab].direction === SortTypeEnum.DESC ? SortTypeEnum.ASC : SortTypeEnum.DESC,
      },
    }));
  };

  const getSortStatus = (key: SortConfig['key']): SortTypeEnum =>
    sortConfigs[currentTab].key === key ? sortConfigs[currentTab].direction : SortTypeEnum.DEFAULT;

  const sortedTokens = useMemo(
    () => ({
      trendingList: [...trendingList].sort((a, b) => compareValues(a, b, sortConfigs.trending.key)),
      followList: [...followList].sort((a, b) => compareValues(a, b, sortConfigs.watchList.key)),
    }),
    [trendingList, followList, sortConfigs],
  );

  const fetchTrendingData = async () => {
    try {
      setTrendingLoading(true);
      const res = await tokenApiService.getTokenTrendList(queryParams);
      if (res.code === 0) {
        setTrendingList(res.data.items);
      }
    } catch (error) {
      console.error('Trending data fetch failed:', error);
    } finally {
      setTrendingLoading(false);
    }
  };
  useEffect(() => {
    fetchTrendingData();
    const timer = setInterval(() => {
      fetchTrendingData();
    }, 15000);
    return () => {
      clearInterval(timer);
    };
  }, [queryParams, chain]);

  const fetchFollowData = async () => {
    if (!walletAddress || !isConnected) return;

    try {
      setFollowLoading(true);
      const res = await userApiService.getFollowTokenListApi({ walletAddress, timeRange });
      if (res.code === 0) {
        const tokenAddressList = res.data.map((item) => item.address.toLocaleLowerCase());
        setFollowTokenAddressList(tokenAddressList);
        setFollowList(res.data);
      }
    } catch (error: any) {
      if (error.code === 401) {
        setFollowList([]);
      }
    } finally {
      setFollowLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowData();
  }, [walletAddress, isConnected, chain]);

  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== activeIndex) {
      swiperRef.current.slideTo(activeIndex);
    }
  }, [activeIndex]);

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Follow_Token, () => {
      fetchFollowData();
    });
    return () => unsubscribe();
  }, [fetchFollowData]);

  return (
    <div className="relative">
      {isConnected && <AccountInfo />}
      <div className="flex justify-between items-center mb-[12px]">
        <CustomTab wrapperClassName="mt-[20px]" tabs={tabs} activeIndex={activeIndex} onTabChange={setActiveIndex} />
        <Select value={timeRange} onValueChange={(e: TokenTimeRange) => setTimeRange(e)}>
          <SelectTrigger className="w-[75px] h-[36px] p-[8px] bg-primaryBgColor rounded-[8px]">
            <SelectValue
              className="bg-[#fff] w-full h-full text-[12px] font-[400] border-[1px] border-[#E4E4E7] dark:border-[#27272A]"
              placeholder="1H"
            />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {TOKEN_TIME_TAB_CONFIG.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <TokenHead getSortStatus={getSortStatus} handleSort={handleSort} />
      <Swiper
        className="w-full h-full"
        initialSlide={activeIndex}
        onSlideChange={(e) => setActiveIndex(e.activeIndex)}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
      >
        <SwiperSlide>
          <TokenList loading={trendingLoading} sortedTokens={sortedTokens.trendingList} />
        </SwiperSlide>
        {isConnected && (
          <SwiperSlide>
            <TokenList loading={followLoading} sortedTokens={sortedTokens.followList} />
          </SwiperSlide>
        )}
      </Swiper>
    </div>
  );
};
